import * as React from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils/ui";
import { Activity, Dumbbell, PencilIcon } from "lucide-react";
import { FaRunning } from "react-icons/fa";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { MetricLineChart } from "@/components/charts/metricsLineChart";
import CardioPlanDialog from "@/components/activityComponents/CardioPlanDialog";
import { CardioActivityDialog } from "@/components/activityComponents/CardioActivityDialog";
import * as luxon from "luxon";
import { MetricDetails } from "./metrics";
import LoadingSpinner from "@/components/LoadingSpinner";
import { ClientWorkoutLogs } from "@/components/client/logs/ClientWorkoutLogs";
import { trpc } from "@/lib/trpc/client";
import { Metric } from "@/components/metricsComponents/metricsColumns";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { VirtualizedLogTable } from "@/components/client/logs/VirtualizedLogTable";
import { Button } from "@/components/ui/button";
import { ClientSidebarDetailLoadingState } from "@/components/client/ClientTabLoadingStates";
import { useWeekStartsOn } from "@/lib/dates/useWeekStartsOn";
import { luxonStartOfWeek } from "@/lib/dates/weekStart";
import {
  CARDIO_SOURCE_LABELS,
  formatDistance,
  formatDurationMinutes,
  isCardioActivitySource,
} from "@trainrr/api/cardio-activity";

export const Route = createFileRoute(
  "/_auth/_dashboard/clients/$clientId/_layout/activity",
)({
  component: ActivityPage,
  pendingComponent: ClientSidebarDetailLoadingState,
});

const tabs = [
  {
    name: "Steps",
    image: FaRunning,
    value: "steps",
  },
  {
    name: "Cardio",
    image: Activity,
    value: "cardio",
  },
  {
    name: "Workouts",
    image: Dumbbell,
    value: "workouts",
  },
];

const STEPS_METRIC_ID = "03a40121-31c4-42da-b259-a60781dcb32e";

type ActivitySummary = {
  currentValue?: number;
  changeValue?: number;
  changePercent?: number;
  sign: -1 | 0 | 1;
  unit?: string;
};

type ActivityTabValue = (typeof tabs)[number]["value"];

function ActivityPage() {
  const { clientId } = Route.useParams();

  const [selectedTab, setSelectedTab] = React.useState("steps");
  const activitySummaries = useActivitySummaries(clientId);

  return (
    <>
      <Tabs defaultValue="cardio" value={selectedTab}>
        <div className="w-full md:w-[90%] mx-auto px-4 lg:px-0">
          {/* Mobile Dropdown */}
          <div className="lg:hidden mb-4">
            <Select value={selectedTab} onValueChange={setSelectedTab}>
              <SelectTrigger className="h-10 w-full">
                <SelectValue placeholder="Select activity type" />
              </SelectTrigger>
              <SelectContent>
                {tabs.map((tab) => (
                  <SelectItem key={tab.value} value={tab.value}>
                    <span className="flex items-center gap-2">
                      <tab.image size={16} />
                      {tab.name}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="w-full grid grid-cols-1 lg:grid-cols-[19rem_minmax(0,1fr)] gap-5">
            <Card className="hidden lg:flex sticky top-5 h-[calc(100svh-5.25rem)] max-h-[calc(100svh-5.25rem)] flex-col overflow-hidden">
              <div className="flex items-center justify-between gap-2 px-4 pt-4 pb-3">
                <h2 className="text-lg font-semibold tracking-tight">
                  Activity
                </h2>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4">
                <div className="flex flex-col gap-2">
                  {tabs.map((tab) => {
                    const summary = activitySummaries[tab.value];
                    const selected = selectedTab === tab.value;
                    return (
                      <button
                        key={tab.value}
                        type="button"
                        className={cn(
                          "flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors hover:bg-muted/50",
                          selected
                            ? "border-primary bg-primary/5"
                            : "border-border bg-card",
                        )}
                        onClick={() => setSelectedTab(tab.value)}
                      >
                        <tab.image
                          size={18}
                          className="shrink-0 text-muted-foreground"
                        />
                        <div className="min-w-0 flex-1">
                          <h3 className="truncate font-medium leading-snug">
                            {tab.name}
                          </h3>
                          <div className="mt-2 flex min-w-0 items-baseline justify-between gap-2">
                            <span className="truncate text-sm text-muted-foreground">
                              {formatActivityCurrentValue(summary)}
                            </span>
                            {summary.currentValue !== undefined &&
                              summary.sign !== 0 && (
                                <ActivityChangeInline
                                  summary={summary}
                                  className="shrink-0 text-sm"
                                />
                              )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </Card>
            <div className="min-w-0">
              <TabsContent value="steps" className="w-full mt-0">
                <StepsDetails clientId={clientId} />
              </TabsContent>
              <TabsContent value="cardio" className="w-full mt-0">
                <CardioDetails clientId={clientId} />
              </TabsContent>
              <TabsContent value="workouts" className="w-full mt-0">
                <ClientWorkoutLogs clientId={clientId} />
              </TabsContent>
            </div>
          </div>
        </div>
      </Tabs>
    </>
  );
}

interface CardioDetailsProps {
  clientId: string;
}

function useActivitySummaries(
  clientId: string,
): Record<ActivityTabValue, ActivitySummary> {
  const now = React.useMemo(() => luxon.DateTime.local(), []);
  const weekStartsOn = useWeekStartsOn();
  const currentWeekStart = React.useMemo(
    () => luxonStartOfWeek(now, weekStartsOn),
    [now, weekStartsOn],
  );
  const previousWeekStart = React.useMemo(
    () => currentWeekStart.minus({ weeks: 1 }),
    [currentWeekStart],
  );

  const { data: stepsLogs } = trpc.metrics.listLogs.useQuery(
    { clientId, metricId: STEPS_METRIC_ID },
    { enabled: Boolean(clientId) },
  );
  const { data: cardioLogs } = trpc.cardio.listLogs.useQuery(
    {
      clientId,
      fromLoggedAt: previousWeekStart.toISO() ?? undefined,
    },
    { enabled: Boolean(clientId) },
  );
  const previousWeekStartIso = previousWeekStart.toISO();
  const { data: workoutLogs } = trpc.workouts.history.useQuery(
    {
      clientId,
      startFrom: previousWeekStartIso ?? undefined,
    },
    { enabled: Boolean(clientId && previousWeekStartIso) },
  );

  const steps = React.useMemo(
    () =>
      getSeriesSummary(
        (stepsLogs ?? []).map((log) => ({
          value: log.value,
          date: log.logged_at,
        })),
        "steps",
      ),
    [stepsLogs],
  );

  const cardio = React.useMemo(
    () =>
      getSeriesSummary(
        (cardioLogs ?? []).map((log) => ({
          value: log.duration / 60,
          date: log.logged_at,
        })),
        "mins",
      ),
    [cardioLogs],
  );

  const workouts = React.useMemo(() => {
    const logs = workoutLogs ?? [];
    const currentWeekCount = logs.filter((log) => {
      const date = luxon.DateTime.fromISO(log.start_time);
      return date >= currentWeekStart;
    }).length;
    const previousWeekCount = logs.filter((log) => {
      const date = luxon.DateTime.fromISO(log.start_time);
      return date >= previousWeekStart && date < currentWeekStart;
    }).length;
    const changeValue = currentWeekCount - previousWeekCount;
    const changePercent =
      previousWeekCount > 0 ? (changeValue / previousWeekCount) * 100 : 0;
    const sign = changeValue > 0 ? 1 : changeValue < 0 ? -1 : 0;

    return {
      currentValue: currentWeekCount,
      changeValue,
      changePercent,
      sign,
      unit: currentWeekCount === 1 ? "workout" : "workouts",
    } satisfies ActivitySummary;
  }, [currentWeekStart, previousWeekStart, workoutLogs]);

  return { steps, cardio, workouts };
}

function getSeriesSummary(
  logs: { value: number; date: string }[],
  unit: string,
): ActivitySummary {
  if (logs.length === 0) {
    return { sign: 0, unit };
  }

  const sortedLogs = [...logs].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
  );
  const start = sortedLogs[0]?.value ?? 0;
  const current = sortedLogs[sortedLogs.length - 1]?.value ?? 0;
  const changeValue = current - start;
  const changePercent = start !== 0 ? (changeValue / Math.abs(start)) * 100 : 0;
  const sign = changeValue > 0 ? 1 : changeValue < 0 ? -1 : 0;

  return {
    currentValue: current,
    changeValue,
    changePercent,
    sign,
    unit,
  };
}

function formatActivityCurrentValue(summary: ActivitySummary) {
  if (summary.currentValue === undefined) {
    return "No logs";
  }

  const value = Number.isInteger(summary.currentValue)
    ? summary.currentValue.toString()
    : summary.currentValue.toFixed(1);

  return `${value}${summary.unit ? ` ${summary.unit}` : ""}`;
}

function ActivityChangeInline({
  summary,
  className,
}: {
  summary: ActivitySummary;
  className?: string;
}) {
  if (summary.changeValue === undefined || summary.sign === 0) {
    return null;
  }

  const formatted = Number.isInteger(summary.changeValue)
    ? Math.abs(summary.changeValue).toLocaleString()
    : Math.abs(summary.changeValue).toFixed(1);
  const signed =
    summary.changeValue > 0
      ? `+${formatted}`
      : `-${formatted}`;
  const tone =
    summary.sign > 0
      ? "text-emerald-600 dark:text-emerald-400"
      : "text-rose-600 dark:text-rose-400";

  return (
    <span className={cn("font-semibold", tone, className)}>
      {signed}
      {summary.unit ? ` ${summary.unit}` : ""}
    </span>
  );
}

/** Covers well over a year of daily activities before a coach has to page. */
const LOGS_PAGE_SIZE = 200;
/**
 * The chart plots whatever has loaded, so pages are pulled in automatically up
 * to this many. Beyond that the coach pages by hand, rather than us firing an
 * unbounded chain of requests at a very active client.
 */
const LOGS_AUTO_PAGES = 3;

const CardioDetails: React.FunctionComponent<CardioDetailsProps> = ({
  clientId,
}) => {
  const [cardioDialog, setCardioDialog] = React.useState(false);
  const [selectedLogId, setSelectedLogId] = React.useState<string | null>(null);

  const [weeksForRange, setWeeksForRange] = React.useState<number | null>(2);

  // Rounded to the day so the value is stable across mounts: it is part of the
  // query key, and a fresh `now()` would discard the loaded pages every time
  // the coach comes back to this tab.
  const fromDate = React.useMemo(() => {
    const now = luxon.DateTime.now().startOf("day");
    if (weeksForRange === null) {
      return now.minus({ days: 400 }).toISO();
    }
    return now.minus({ weeks: weeksForRange }).toISO();
  }, [weeksForRange]);

  const { data: client } = trpc.clients.get.useQuery(
    { clientId },
    { enabled: Boolean(clientId) },
  );
  const distanceUnit = client?.distance_unit === "mi" ? "mi" : "km";

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
    trpc.cardio.listLogs.useInfiniteQuery(
      { clientId, fromLoggedAt: fromDate ?? undefined, limit: LOGS_PAGE_SIZE },
      {
        enabled: Boolean(clientId),
        getNextPageParam: (lastPage) => {
          const last = lastPage[lastPage.length - 1];
          if (!last || lastPage.length < LOGS_PAGE_SIZE) {
            return undefined;
          }
          return { loggedAt: last.logged_at, id: last.id };
        },
      },
    );

  const logs = React.useMemo(() => data?.pages.flat() ?? [], [data]);
  const pageCount = data?.pages.length ?? 0;

  React.useEffect(() => {
    if (hasNextPage && !isFetchingNextPage && pageCount < LOGS_AUTO_PAGES) {
      void fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, pageCount, fetchNextPage]);

  const chartData = React.useMemo(() => {
    return logs
      .map((log) => ({
        date: log.logged_at,
        value: log.duration / 60,
      }))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }, [logs]);

  return (
    <>
      <CardioPlanDialog
        clientId={clientId}
        open={cardioDialog}
        onOpenChange={setCardioDialog}
      />
      <CardioActivityDialog
        clientId={clientId}
        logId={selectedLogId}
        open={Boolean(selectedLogId)}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedLogId(null);
          }
        }}
      />
      <MetricLineChart
        metric={{
          id: "1",
          name: "Cardio",
          unit: "mins",
        }}
        data={chartData}
        onDateRangeWeeksChanged={setWeeksForRange}
        dateRangeWeeksValue={weeksForRange}
        leftButton={{
          buttonIcon: <PencilIcon size={20} />,
          buttontitle: "Edit Plan",
          onLeftClicked: () => {
            setCardioDialog(true);
          },
        }}
      />

      <VirtualizedLogTable
        className={cn("mt-5", hasNextPage ? "mb-3" : "mb-10")}
        data={logs}
        getRowKey={(log) => log.id ?? log.logged_at}
        rowClassName="h-14"
        minWidth="48rem"
        onRowClick={(log) => {
          if (log.id) {
            setSelectedLogId(log.id);
          }
        }}
        columns={[
          {
            key: "name",
            header: "Name",
            cell: (log) => log.session_name,
          },
          {
            key: "date",
            header: "Date",
            cell: (log) =>
              luxon.DateTime.fromISO(log.logged_at).toLocaleString(
                luxon.DateTime.DATE_MED,
              ),
          },
          {
            key: "duration",
            header: "Duration",
            cell: (log) => formatDurationMinutes(log.duration),
          },
          {
            key: "distance",
            header: "Distance",
            cell: (log) => formatDistance(log.distance, distanceUnit) ?? "—",
          },
          {
            key: "heart-rate",
            header: "Heart Rate",
            cell: (log) =>
              log.heart_rate != null ? Math.round(log.heart_rate) : "—",
          },
          {
            key: "calories",
            header: "Calories",
            cell: (log) =>
              log.calories != null ? Math.round(log.calories) : "—",
          },
          {
            key: "source",
            header: "Source",
            cell: (log) =>
              isCardioActivitySource(log.source)
                ? CARDIO_SOURCE_LABELS[log.source]
                : log.source,
          },
        ]}
      />
      {hasNextPage ? (
        <div className="mb-10 flex flex-col items-center gap-2">
          <p className="text-muted-foreground text-xs">
            Showing the {logs.length} most recent activities in this range.
          </p>
          <Button
            variant="outline"
            size="sm"
            disabled={isFetchingNextPage}
            onClick={() => void fetchNextPage()}
          >
            {isFetchingNextPage ? "Loading…" : "Load older activities"}
          </Button>
        </div>
      ) : null}
    </>
  );
};

const StepsDetails: React.FunctionComponent<CardioDetailsProps> = ({
  clientId,
}) => {
  const { data, isLoading } = trpc.metrics.listForClient.useQuery(
    { clientId },
    { enabled: Boolean(clientId) },
  );

  const stepsMetric = React.useMemo((): Metric | undefined => {
    const assigned = data?.find((metric) => metric.id === STEPS_METRIC_ID);
    if (!assigned) return undefined;
    return {
      id: assigned.id,
      name: assigned.name ?? "",
      unit: assigned.unit,
      org_id: assigned.org_id ?? "",
      os_integration_type: assigned.os_integration_type,
      daily_target: assigned.daily_target,
    };
  }, [data]);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center mt-20">
        <LoadingSpinner className="size-8" />
      </div>
    );
  }

  if (!stepsMetric) {
    return (
      <div className="flex flex-col items-center justify-center mt-20">
        <h1 className="text-2xl font-bold">No data</h1>
      </div>
    );
  }

  return (
    <MetricDetails
      metric={stepsMetric}
      clientId={clientId}
      dailyTargetDialogClassName="sm:max-w-[480px]"
    />
  );
};

export default CardioDetails;
